/** "Team": who's on it and what they may do; the owner invites and confirms who approves. */
import React, { useEffect, useState } from "react";
import { Building2, Check, Sparkles, UserPlus, X } from "lucide-react";
import {
  useConfirmApprovers,
  useInvitePartner,
  useInviteTeammate,
  useMyTeam,
  useTeamActivity,
} from "@/services/python-api/customerHooks";
import { Avatar, Card, Loading, Page, Pill, ROLE_LABEL, ROLE_TONE } from "./parts";

function InviteDialog({ onClose }) {
  const invite = useInviteTeammate();
  const [form, setForm] = useState({ name: "", email: "", role: "member" });
  const error = invite.error?.response?.data?.detail;
  const send = (e) => {
    e.preventDefault();
    invite.mutate(form, { onSuccess: onClose });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 sm:items-center" onClick={onClose}>
      <form
        onSubmit={send}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[440px] rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] font-semibold text-[#101928]">Invite a teammate</h2>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={20} color="#667185" />
          </button>
        </div>
        <p className="mt-1 text-[14px] text-[#667185]">They join once they sign in with this email.</p>
        <label className="mt-5 block text-[13px] font-medium text-[#344054]">Name</label>
        <input
          className="mt-1.5 h-11 w-full rounded-xl border border-[#D0D5DD] px-3 text-[15px] outline-none focus:border-[#2563EB]"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Otunba Tola"
        />
        <label className="mt-4 block text-[13px] font-medium text-[#344054]">Work email</label>
        <input
          required
          type="email"
          className="mt-1.5 h-11 w-full rounded-xl border border-[#D0D5DD] px-3 text-[15px] outline-none focus:border-[#2563EB]"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="name@company.com"
        />
        <p className="mt-4 text-[13px] font-medium text-[#344054]">What they can do</p>
        <div className="mt-1.5 space-y-2">
          {[
            ["member", "See every car, paper and invoice, and ask Dockie"],
            ["approver", "Also say yes to money and promises"],
          ].map(([role, what]) => (
            <button
              type="button"
              key={role}
              onClick={() => setForm({ ...form, role })}
              className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left ${
                form.role === role ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E4E7EC]"
              }`}
            >
              <span>
                <span className="block text-[14px] font-medium text-[#101928]">{ROLE_LABEL[role]}</span>
                <span className="block text-[13px] text-[#667185]">{what}</span>
              </span>
              {form.role === role ? <Check size={18} color="#2563EB" /> : null}
            </button>
          ))}
        </div>
        {error ? <p className="mt-3 text-[13px] text-[#CB1A14]">{error}</p> : null}
        <button
          disabled={invite.isPending}
          className="mt-6 h-11 w-full rounded-full bg-[#2563EB] text-[15px] font-medium text-white disabled:opacity-60"
        >
          Send invite
        </button>
      </form>
    </div>
  );
}

function PartnerDialog({ onClose }) {
  const invite = useInvitePartner();
  const [form, setForm] = useState({ company: "", name: "", email: "", kind: "forwarder" });
  const error = invite.error?.response?.data?.detail;
  const field = (key, label, placeholder, type = "text") => (
    <>
      <label className="mt-4 block text-[13px] font-medium text-[#344054]">{label}</label>
      <input
        required={key !== "name"}
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1.5 h-11 w-full rounded-xl border border-[#D0D5DD] px-3 text-[15px] outline-none focus:border-[#2563EB]"
      />
    </>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 sm:items-center" onClick={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate(form, { onSuccess: onClose });
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[440px] rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] font-semibold text-[#101928]">Invite your forwarder or agent</h2>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={20} color="#667185" />
          </button>
        </div>
        <p className="mt-1 text-[14px] text-[#667185]">
          They see your cars and papers and can upload dock receipts and bills of lading. Never your invoices,
          decisions or team.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {[
            ["forwarder", "Freight forwarder"],
            ["clearing_agent", "Clearing agent"],
          ].map(([kind, label]) => (
            <button
              type="button"
              key={kind}
              onClick={() => setForm({ ...form, kind })}
              className={`h-10 rounded-xl border text-[14px] ${
                form.kind === kind ? "border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]" : "border-[#E4E7EC] text-[#344054]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {field("company", "Company", "Sea & Shore Shipping")}
        {field("name", "Contact name", "Kemi Adebayo")}
        {field("email", "Their work email", "ops@seashore.example", "email")}
        {error ? <p className="mt-3 text-[13px] text-[#CB1A14]">{error}</p> : null}
        <button
          disabled={invite.isPending}
          className="mt-6 h-11 w-full rounded-full bg-[#2563EB] text-[15px] font-medium text-white disabled:opacity-60"
        >
          Send invite
        </button>
      </form>
    </div>
  );
}

function Suggestions({ members }) {
  const confirm = useConfirmApprovers();
  const suggested = members.filter((m) => m.suggested_role === "approver");
  const [picked, setPicked] = useState(suggested.map((m) => m.id));
  useEffect(() => {
    setPicked(suggested.map((m) => m.id));
  }, [suggested.length]); // eslint-disable-line
  if (!suggested.length) return null;
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <Card className="border-[#BFDBFE] bg-[#F8FBFF]">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-full bg-[#DBEAFE] p-1.5">
          <Sparkles size={16} color="#2563EB" />
        </span>
        <div>
          <p className="text-[15px] font-semibold text-[#101928]">Who else should approve money?</p>
          <p className="mt-0.5 text-[13px] text-[#475367]">
            Dockie noticed these teammates saying yes to money in your room. Until you confirm, only owners approve.
          </p>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {suggested.map((m) => (
          <button
            key={m.id}
            onClick={() => toggle(m.id)}
            className="flex w-full items-center gap-3 rounded-xl bg-white px-3 py-2.5 text-left"
          >
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                picked.includes(m.id) ? "border-[#2563EB] bg-[#2563EB]" : "border-[#D0D5DD]"
              }`}
            >
              {picked.includes(m.id) ? <Check size={14} color="white" /> : null}
            </span>
            <Avatar name={m.name} size={28} />
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-medium text-[#101928]">{m.name}</span>
              <span className="block text-[12px] text-[#667185]">{m.suggested_because}</span>
            </span>
          </button>
        ))}
      </div>
      <button
        disabled={confirm.isPending}
        onClick={() => confirm.mutate(picked)}
        className="mt-3 h-10 w-full rounded-full bg-[#2563EB] text-[14px] font-medium text-white disabled:opacity-60"
      >
        {picked.length ? `Make ${picked.length} ${picked.length === 1 ? "approver" : "approvers"}` : "None of them"}
      </button>
    </Card>
  );
}

function Activity() {
  const { data, isLoading } = useTeamActivity();
  const lines = data?.lines ?? [];
  return (
    <Card className="py-2">
      <p className="px-0 pt-2 text-[15px] font-semibold text-[#101928]">Team activity</p>
      {isLoading ? <div className="py-6"><Loading /></div> : null}
      <ul className="mt-1 divide-y divide-[#F0F2F5]">
        {lines.slice(0, 8).map((l, i) => (
          <li key={i} className="flex gap-3 py-3">
            <Avatar name={l.who} size={28} />
            <div className="min-w-0 flex-1">
              <p className="text-[14px] text-[#101928]">
                <span className="font-medium">{l.who}</span>{" "}
                <span className="text-[#667185]">
                  {l.kind === "decided" ? "answered" : "asked"} about {l.car_name ?? "a car"} …{l.car}
                </span>
              </p>
              <p className="line-clamp-1 text-[13px] text-[#475367]">{l.what}</p>
            </div>
            <span className="shrink-0 text-[12px] text-[#98A2B3]">{l.when}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function MyTeam() {
  const { data, isLoading } = useMyTeam();
  const [inviting, setInviting] = useState(false);
  const [invitingPartner, setInvitingPartner] = useState(false);
  const members = data?.members ?? [];
  const invite = data?.me?.can_manage ? (
    <button
      onClick={() => setInviting(true)}
      className="flex h-9 items-center gap-1.5 rounded-full bg-[#2563EB] px-4 text-[14px] font-medium text-white"
    >
      <UserPlus size={16} /> Invite
    </button>
  ) : null;
  return (
    <Page title="Team" actions={invite}>
      {isLoading || !data ? (
        <Loading />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between lg:hidden">
            <div>
              <p className="text-[20px] font-semibold text-[#101928]">{data.company}</p>
              <p className="text-[13px] text-[#667185]">{members.length} people</p>
            </div>
            {invite}
          </div>
          <p className="hidden text-[14px] text-[#667185] lg:block">
            {data.company} · {members.length} people. Everyone sees every car, paper and invoice; approvers say yes
            to money.
          </p>
          {data.suggestions_waiting ? <Suggestions members={members} /> : null}
          <Card className="divide-y divide-[#F0F2F5] py-1">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 py-3">
                <Avatar name={m.name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-medium text-[#101928]">
                    {m.name} {m.me ? <span className="font-normal text-[#98A2B3]">· You</span> : null}
                  </p>
                  <p className="truncate text-[13px] text-[#667185]">
                    {m.joined ? m.email : `Invited · ${m.email}`}
                  </p>
                </div>
                <Pill tone={ROLE_TONE[m.role]}>{ROLE_LABEL[m.role]}</Pill>
              </div>
            ))}
          </Card>
          {data.me?.can_manage ? (
            <button
              onClick={() => setInvitingPartner(true)}
              className="flex w-full items-center gap-3 rounded-2xl border border-dashed border-[#D0D5DD] px-4 py-3 text-left hover:bg-[#F9FAFB]"
            >
              <span className="rounded-full bg-[#FEF6E7] p-2">
                <Building2 size={18} color="#865503" />
              </span>
              <span>
                <span className="block text-[15px] font-medium text-[#101928]">Invite your forwarder or clearing agent</span>
                <span className="block text-[13px] text-[#667185]">They see your cars and papers, and can upload papers</span>
              </span>
            </button>
          ) : null}
          <Activity />
        </div>
      )}
      {inviting ? <InviteDialog onClose={() => setInviting(false)} /> : null}
      {invitingPartner ? <PartnerDialog onClose={() => setInvitingPartner(false)} /> : null}
    </Page>
  );
}
